import {create} from "zustand";
import {cameraToWorld, clampZoom, PosRef, VisionBoardBag} from "./canvas-utils";
import {useEffect, useRef, useState} from "react";
import {setupDragHandlers} from "../../lib/hooks/use-drag";
import {createCanvasSelectionStore} from "./vision-board-node-selection-store";
import {VisionBoardNodeId} from "../../cdx-models/utils/extended-fields";
import CanvasSelectionBox from "./CanvasSelectionBox";
import {detectScrollDevice, useEsc} from "@cdx/common";
import {singleKeyLocalStorage} from "../../lib/storage";
import {isEventWithinInteractiveNode} from "@cdx/common/dom-helpers";

const cursorStore = create<{isMoving: boolean; isSpaceDown: boolean}>(() => ({
  isMoving: false,
  isSpaceDown: false,
}));

export const useGrabToPan = (
  posRef: PosRef,
  positionCanvas: () => void,
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  onSelection: (idSet: Set<VisionBoardNodeId>) => void
) => {
  useEffect(() => {
    return cursorStore.subscribe((s) => {
      const node = wrapperRef.current;
      if (node) node.style.cursor = s.isMoving ? "grabbing" : s.isSpaceDown ? "grab" : "";
    });
  }, [wrapperRef]);

  const spaceBarPressedRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEventWithinInteractiveNode(e)) return;
      if (e.code === "Space") {
        e.preventDefault();
        if (!e.repeat) {
          spaceBarPressedRef.current = true;
          cursorStore.setState({isSpaceDown: true});
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isEventWithinInteractiveNode(e)) return;
      if (e.code === "Space") {
        spaceBarPressedRef.current = false;
        cursorStore.setState({isSpaceDown: false});
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const [selectionStart, setSelectionStart] = useState<[number, number] | null>(null);
  const selectionStoreCbRef = useRef(onSelection);
  selectionStoreCbRef.current = onSelection;
  const [selectionStore] = useState(() =>
    createCanvasSelectionStore((idSet) => selectionStoreCbRef.current(idSet))
  );

  const selectionMousePosCbRef = useRef<((pos: [number, number]) => void) | null>(null);

  const [{handlers, unsubs}] = useState(() =>
    setupDragHandlers(
      (startPos, mode: "move" | "select") => {
        const startCanvasPos = [posRef.current.x, posRef.current.y];
        if (mode === "move") {
          return {
            onMove: ([x, y]) => {
              const dx = x - startPos[0];
              const dy = y - startPos[1];
              posRef.current.x = startCanvasPos[0] + dx / posRef.current.scale;
              posRef.current.y = startCanvasPos[1] + dy / posRef.current.scale;
              positionCanvas();
            },
            onStop: () => {
              cursorStore.setState({isMoving: false});
            },
          };
        } else {
          setSelectionStart(startPos);
          return {
            onMove: (pos) => {
              selectionMousePosCbRef.current?.(pos);
            },
            onStop: (isCancelled) => {
              setSelectionStart(null);
            },
          };
        }
      },
      (e) => {
        const getMode = () => {
          if ("button" in e && e.button === 2) return "move";
          return spaceBarPressedRef.current ? "move" : "select";
        };
        const mode = getMode();
        if (mode === "move") {
          cursorStore.setState({isMoving: true});
        } else {
          selectionStore.clear();
        }
        return {
          onDone: () => {
            return mode;
          },
          onCancel: () => {
            cursorStore.setState({isMoving: false});
          },
        };
      },
      {allowSecondaryButton: true}
    )
  );
  useEffect(() => {
    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [unsubs]);

  useEsc(() => {
    if (!selectionStore.clear()) return false;
  });

  return {
    selectionStore,
    handlers,
    isSelecting: Boolean(selectionStart),
    renderSelectionBox: (bag: VisionBoardBag) =>
      selectionStart && (
        <CanvasSelectionBox
          startPos={selectionStart}
          onMousePosCbRef={selectionMousePosCbRef}
          bag={bag}
        />
      ),
  };
};

let storedInLocalStorage = false;
const wheelModeStorage = singleKeyLocalStorage<"mouse" | "touchpad">("wheel-mode");

const useWheelModeDetector = () => {
  const ref = useRef<null | "mouse" | "touchpad">(null);
  const firstRef = useRef(true);
  return (e: WheelEvent): "mouse" | "touchpad" => {
    if (!ref.current) {
      const mode = detectScrollDevice(e, {isFirst: firstRef.current});
      firstRef.current = false;
      if (mode !== null) {
        ref.current = mode;
        if (!storedInLocalStorage) {
          storedInLocalStorage = true;
          wheelModeStorage.set(mode);
        }
        return mode;
      } else {
        return wheelModeStorage.get() ?? "mouse";
      }
    }
    return ref.current;
  };
};

export const useWheel = (
  posRef: PosRef,
  positionCanvas: () => void,
  wrapperRef: React.RefObject<HTMLDivElement>
) => {
  const detectWheelMode = useWheelModeDetector();

  const handleWheel = (e: WheelEvent) => {
    const {deltaMode, shiftKey, ctrlKey, metaKey} = e;
    // needed to prevent native browser zoom when ctrl is pressed?
    e.stopPropagation();
    e.preventDefault();
    const getDelta = () => {
      const baseX = shiftKey
        ? Math.abs(e.deltaY) > Math.abs(e.deltaX)
          ? e.deltaY
          : e.deltaX
        : e.deltaX;
      const baseY = shiftKey ? 0 : e.deltaY;
      switch (deltaMode) {
        case e.DOM_DELTA_LINE:
          return [baseX * 40, baseY * 40];
        case e.DOM_DELTA_PAGE:
          return [baseX * 800, baseY * 800];
        default:
          return [baseX, baseY];
      }
    };
    const [dx, dy] = getDelta();
    const getMode = () => {
      if (ctrlKey || metaKey) return "zoom";
      return detectWheelMode(e) === "mouse" ? "zoom" : "pan";
    };
    const mode = getMode();
    if (mode === "zoom") {
      const scaleDelta = dy * 0.002;
      const next = clampZoom(posRef.current.scale * (1 - scaleDelta));
      const ratio = posRef.current.scale / next;

      const [mx, my] = cameraToWorld([e.pageX, e.pageY], posRef);
      posRef.current.y = ratio * (my + posRef.current.y) - my;
      posRef.current.x = ratio * (mx + posRef.current.x) - mx;

      posRef.current.scale = next;
    } else {
      posRef.current.x += -dx / posRef.current.scale;
      posRef.current.y += -dy / posRef.current.scale;
    }
    positionCanvas();
  };

  const wheelHandlerRef = useRef(handleWheel);
  wheelHandlerRef.current = handleWheel;

  useEffect(() => {
    document.body.style.overscrollBehavior = "none";
    const el = wrapperRef.current!;
    const handler = (e: WheelEvent) => wheelHandlerRef.current(e);
    el.addEventListener("wheel", handler, {passive: false});
    return () => {
      document.body.style.overscrollBehavior = "";
      el.removeEventListener("wheel", handler);
    };
  }, [wrapperRef]);
};
